import React, { useState } from 'react';
import PropTypes from 'prop-types';

import './CardList.css';

import { useSet } from '../../hook/useSet';
import { buildClass } from '../../libs/classes';
import { SimpleSelectLabeled } from '../SimpleComponents/SimpleSelect';
import OrderingSwitch, { orderType } from '../SimpleComponents/OrderingSwitch';

const SORT_TYPES = {
	number: 'number',
	name: 'name',
	value: 'value',
};

const CardsList = ({ cards, activeCard, onSelectCard }) => {
	const { sets } = useSet();

	const [sortType, setSortType] = useState(SORT_TYPES.number);
	const [ordering, setOrdering] = useState(orderType.ascending);

	const handleSortChange = (event) => {
		setSortType(event.target.value);
	};

	const handleSwitchOrdering = () => {
		const orderingUpd = ordering === orderType.ascending ? orderType.descending : orderType.ascending;
		setOrdering(orderingUpd);
	};

	const sortCards = (card1, card2) => {
		let result = 0;
		switch (sortType) {
			case SORT_TYPES.name:
				result = card1.name > card2.name ? 1 : -1;
				break;
			case SORT_TYPES.value: {
				let p1 = 0;
				if (card1.price_usd && card1.price_usd !== '0.00') {
					p1 = parseFloat(card1.price_usd);
				} else if (card1.price_usd_foil) {
					p1 = parseFloat(card1.price_usd_foil);
				} else if (card1.price_usd_etched) {
					p1 = parseFloat(card1.price_usd_etched);
				}
				let p2 = 0;
				if (card2.price_usd && card2.price_usd !== '0.00') {
					p2 = parseFloat(card2.price_usd);
				} else if (card2.price_usd_foil) {
					p2 = parseFloat(card2.price_usd_foil);
				} else if (card2.price_usd_etched) {
					p2 = parseFloat(card2.price_usd_etched);
				}
				result = p1 - p2;
				break;
			}
			case SORT_TYPES.number:
			default:
				// Default sort by number
				result = parseInt(card1.c_number) > parseInt(card2.c_number) ? 1 : -1;
				break;
		}
		return ordering === orderType.ascending ? result : -result;
	};

	const buildPrices = (card) => {
		return (
			<div className="card-prices">
				{card.etched ? (
					<span className="price-holder etched" title="etched">
						{card.price_usd_etched != '0.00' ? `$${card.price_usd_etched}` : '?'}
					</span>
				) : (
					<span className="price-holder" />
				)}
				{card.foil ? (
					<span className="price-holder foil" title="foil">
						{card.price_usd_foil != '0.00' ? `$${card.price_usd_foil}` : '?'}
					</span>
				) : (
					<span className="price-holder" />
				)}
				{card.nonfoil ? (
					<span className="price-holder nonfoil" title="non-foil">
						{card.price_usd != '0.00' ? `$${card.price_usd}` : '?'}
					</span>
				) : (
					<span className="price-holder" />
				)}
			</div>
		);
	};

	return (
		<div className="cards-list content scrollable">
			<div className="subhead">
				<div className="spacer" />
				<div className="sorting">
					<SimpleSelectLabeled label="Sort:" value={sortType} onChange={handleSortChange}>
						<option value={SORT_TYPES.number}>Card Number</option>
						<option value={SORT_TYPES.name}>Card Name</option>
						<option value={SORT_TYPES.value}>Card Value</option>
					</SimpleSelectLabeled>
					<OrderingSwitch state={ordering} onClick={handleSwitchOrdering} />
				</div>
			</div>
			{cards && Object.keys(cards).length ? (
				Object.keys(cards)
					// .filter((cardId) => (filterFn ? filterFn(cards[cardId]) : true))
					.sort((cardId1, cardId2) => sortCards(cards[cardId1], cards[cardId2]))
					.map((cardId) => (
						<div
							className={buildClass('card-item', 'row').add(
								activeCard && cardId == activeCard.id,
								'selected'
							)}
							key={cardId}
							onClick={() => onSelectCard(cards[cardId])}
						>
							<div className="card-number">
								<span className="card-set-code">{sets[cards[cardId].set_id]?.code}</span>
								&thinsp;<span className="card-c-number">{cards[cardId].c_number}</span>
							</div>
							<div className={buildClass('card-name', `rarity-${cards[cardId].rarity}`)}>
								{cards[cardId].name}
							</div>

							<div className="card-nonfoil-price">{buildPrices(cards[cardId])}</div>
						</div>
					))
			) : (
				<div className="no-cards">No cards in this set</div>
			)}
		</div>
	);
};

CardsList.propTypes = {
	// Associative array (Object) of cards
	cards: PropTypes.objectOf(PropTypes.object).isRequired,
	// Currently active/selected card
	activeCard: PropTypes.object,
	// Function to call when a card is activated
	onSelectCard: PropTypes.func.isRequired,
};

export default CardsList;
